/**
 * Report Theme — user-customizable colors, fonts, and branding for SiteRAG reports.
 * Persisted to Firestore and localStorage.
 */
import type { CompanyArchetype } from './company';

export interface ReportTheme {
  // Brand
  companyName: string;
  logoUrl: string | null;         // Data URL or Firebase Storage URL

  // Colors
  primaryColor: string;           // e.g. "#D97706" (sidebar, accents)
  headingColor: string;           // e.g. "#0f172a" (section headings)
  bodyTextColor: string;          // e.g. "#475569" (paragraph text)
  backgroundColor: string;        // e.g. "#ffffff" (page background)

  // Typography
  headingFont: string;            // e.g. "Geist Variable", "Inter", "Georgia"
  bodyFont: string;               // e.g. "Geist Variable", "Inter", "system-ui"
  /**
   * Optional display/cover font (theme-preset family). Distinct from
   * `headingFont`: used for the cover title / large display lines when a
   * preset wants a different face there than for section headings.
   *
   * OPTIONAL + spread-merged — absent on legacy themes, so the existing
   * `{...DEFAULT_THEME, ...partial}` Firestore round-trip back-fills it as
   * `undefined` with zero migration. Consumers fall back to `headingFont`
   * when this is unset, so legacy reports render byte-identically.
   */
  displayFont?: string;
  /**
   * Optional APPARATUS font (theme-preset family) — the small,
   * wide-tracked sans face used for the editorial "apparatus": section
   * eyebrows / numerals, micro-labels, figure & table captions, TOC
   * numerals, running heads, and inline citation markers. Distinct from
   * `bodyFont` (the reading serif) and `headingFont`/`displayFont` (the
   * display serif): the academic-editorial house style pairs a serif body
   * with a grotesque sans apparatus (e.g. Hanken Grotesk).
   *
   * OPTIONAL + spread-merged — absent on legacy themes, so the existing
   * `{...DEFAULT_THEME, ...partial}` Firestore round-trip back-fills it as
   * `undefined` with zero migration. The renderer stamps it as the
   * `--report-apparatus-font` CSS var with a `--report-body-font` fallback,
   * so when this is unset the apparatus faces resolve to the body font and
   * legacy reports render byte-identically.
   */
  apparatusFont?: string;

  // Report elements
  sidebarColor: string;           // e.g. "#D97706" (left sidebar bar)
  sidebarText: string;            // e.g. "Strictly Confidential"
  footerText: string;             // e.g. "Datacrate – BTM Feasibility Engine"
  footerSubtext: string;          // e.g. "Confidential – For Authorized Use Only"
  footerEmail: string;            // e.g. "info@datacrate.io"

  // Badges
  positiveColor: string;          // e.g. "#059669" (emerald)
  warningColor: string;           // e.g. "#d97706" (amber)
  negativeColor: string;          // e.g. "#dc2626" (red)

  // ── Layout / pagination ─────────────────────────────────────────
  /** Page size for both the on-screen report frame and the print
   *  stylesheet. Drives @page rules in handleExportPDF. */
  pageSize: 'letter' | 'a4';
  /** Spacing density. 'comfortable' uses the current padding; 'compact'
   *  drops padding and font-size baselines for dense investment-grade
   *  reports. */
  density: 'comfortable' | 'compact';

  // ── Body column layout (two-column academic presets) ──────────────
  /**
   * DEFAULT number of body columns for prose sections.
   *   - `1` (or absent) — single column. The legacy look; byte-stable.
   *   - `2` — two-column academic layout (Quarterly / Review / Proceedings
   *     presets). Prose sections render in two justified columns at a
   *     smaller body type scale; the cover, the executive-summary/abstract
   *     lead, and explicitly full-width sections stay single column;
   *     charts/tables/figures wider than a column span both via
   *     `column-span: all`.
   *
   * ADDITIVE + back-compat: this is only the DEFAULT layout. A section's
   * explicit per-section `sectionColumnLayouts` (template/report override)
   * STILL WINS — `resolveSectionColumnLayout` consumes this only as the
   * fallback when the section has no explicit layout. Absent ⇒ 1 column,
   * so legacy (no-preset) reports are byte-identical.
   */
  bodyColumns?: 1 | 2;

  /**
   * Optional ADDITIVE multiplier applied to the report body font-size in
   * TWO-COLUMN mode only (academic presets render denser/smaller body
   * type). Layered on top of `themeTypographyScale` and BOUNDED so the
   * effective body never drops below the design-token minimum (~12px).
   * Absent ⇒ no extra scaling, so single-column / legacy reports render
   * byte-identically. Tuned per-preset: Quarterly/Review slightly smaller,
   * Proceedings densest.
   */
  bodyTextScale?: number;

  // ── Charts / tables ─────────────────────────────────────────────
  /** Ordered palette for chart series. Index 0 = primary series, then
   *  warning, success, danger, accent. recharts components consume
   *  this via the theme provider. */
  chartPalette: string[];
  /** Table styling — drives the TR/TD borders + striping. */
  tableStyle: 'striped' | 'lined' | 'borderless';

  // ── Typography hierarchy ────────────────────────────────────────
  /** Heading scale baseline. 'editorial' = larger / serif-friendly,
   *  'technical' = tight, 'compact' = the smallest variant. */
  headingScale: 'editorial' | 'technical' | 'compact';

  // ── Cover & TOC ────────────────────────────────────────────────
  /**
   * Cover layout variant — selects which component paints page 1.
   *
   * ADDITIVE (report-cover pack): the journal-aesthetic title pages
   * (`boundary-plate` / `data-snapshot` / `masthead`) extend the original
   * `classic` / `hero-image` / `data-dense` / `minimal` set. Kept in sync
   * with `CoverLayout` in `reportTemplate.ts`. Absent / unknown values fall
   * back to today's default (hero-image) at the renderer, so legacy themes
   * and reports are byte-identical with zero migration. Per-theme-preset
   * defaults: journal → classic, quarterly/review/proceedings → masthead,
   * monograph → minimal, institutional → data-snapshot; an explicit
   * template coverLayout always wins over the preset default.
   */
  coverLayout:
    | 'classic'
    | 'hero-image'
    | 'data-dense'
    | 'minimal'
    | 'boundary-plate'
    | 'data-snapshot'
    | 'masthead'
    /** K5 — additive, opt-in DARK instrument-panel cover (never a preset
     *  default). */
    | 'dark-instrument'
    /** Part L (editorial-layouts-l1) — Editorial-Feature Monograph title page.
     *  Flag-gated (VITE_EDITORIAL_LAYOUTS); the default cover of the `editorial`
     *  preset. Kept in sync with `CoverLayout` in `reportTemplate.ts`. */
    | 'editorial-masthead'
    /** Report Themes v2 (flag-gated VITE_REPORT_THEMES_V2) — the three new title
     *  pages, each the default cover of its matching v2 preset (prospectus →
     *  ProspectusCover, press → FrontispieceCover, signal → TitleBlockCover) and
     *  never resolving elsewhere with the flag OFF. Kept in sync with
     *  `CoverLayout` in `reportTemplate.ts`. */
    | 'prospectus'
    | 'frontispiece'
    | 'title-block'
    /** Golden convergence (GC-W2) — the investment-MEMO title page (MemoCover);
     *  default cover of the `memo` preset / the stock default template. Kept in
     *  sync with `CoverLayout` in `reportTemplate.ts`. */
    | 'memo';
  /** Whether to render the Table of Contents page after the cover. */
  tocEnabled: boolean;

  // ── Site-map figure treatment (theme presets) ─────────────────
  /**
   * How the generated site-layout map / boundary diagram is framed when
   * it renders as a report figure.
   *   - `framed-figure` — bordered frame with a numbered caption
   *     ("Figure N." / "Fig. N ·"). Journal / Monograph default.
   *   - `exhibit`       — titled "EXHIBIT N" header bar above the figure.
   *     Institutional default.
   *   - `full-bleed`    — edge-to-edge, no frame/caption chrome.
   *
   * OPTIONAL + spread-merged — absent on legacy themes; the renderer falls
   * back to today's framing so existing reports are byte-identical. Only
   * a preset (or explicit user choice) sets this.
   */
  siteMapTreatment?: 'framed-figure' | 'exhibit' | 'full-bleed';

  // ── Hero cover sizing (TC2) ────────────────────────────────────
  /** Hero-image cover sizing.
   *  - `full-page` (default when unset — preserves prior look): the hero
   *    fills the whole cover page (`min-h-[500px]`, fills the A4 frame).
   *  - `compact`: the hero renders as a shorter banner so more page-1
   *    body content sits above the fold.
   *  ADDITIVE — when this field is absent the renderer falls back to
   *  `full-page`, so existing covers/reports are byte-identical. */
  heroLayout?: 'full-page' | 'compact';

  // ── Numbering & paginations ────────────────────────────────────
  /** Prefix for figure captions — "Figure ", "Fig. ", etc. */
  figureNumberingPrefix: string;
  /** Page-number format for the running footer. */
  pageNumberFormat: 'arabic' | 'roman-front' | 'page-of-total';

  // ── Compliance copy ─────────────────────────────────────────────
  /** Optional disclaimer text per company archetype. Falls through to
   *  the report's footerSubtext when no archetype-specific override
   *  is present. */
  disclaimerByArchetype: Partial<Record<CompanyArchetype, string>>;

  // ── TSE-2 (C3) Typography scale ────────────────────────────────
  /** Multiplier applied to the base h1/h2/h3/body sizes. Keeps the
   *  existing `headingFont`/`bodyFont` fields as the single source of
   *  truth for font FAMILY — this only scales sizes.
   *  - 0.875 = Compact
   *  - 1     = Normal (default)
   *  - 1.125 = Comfortable
   *  - 1.25  = Large
   */
  themeTypographyScale?: 0.875 | 1 | 1.125 | 1.25;

  // ── TSE-2 (C4) Locale & Units ──────────────────────────────────
  /** Locale & unit preferences. New-tenant fall-through is `undefined`
   *  ⇒ existing formatNumber/formatDate helpers fall back to the
   *  prior US-English defaults (MDY / 1,234.56 / °C / metric / kWh-MWh)
   *  so old reports render byte-identically. */
  themeLocale?: {
    dateFormat?: 'DMY' | 'MDY' | 'YMD' | 'ISO';
    /** Pure visual format — backend stores SI throughout. */
    numberFormat?: '1,234.56' | '1.234,56' | '1 234.56';
    temperatureUnit?: 'C' | 'F';
    distanceUnit?: 'metric' | 'imperial';
    energyUnit?: 'kWh-MWh' | 'GWh';
  };

  // ── TSE-2 (C7) Watermark overlay ───────────────────────────────
  /** Optional diagonal watermark overlaid behind the report content.
   *  Renders via CSS pseudo-element; disabled by default (back-compat). */
  themeWatermark?: {
    enabled?: boolean;
    /** Display text — typically DRAFT / CONFIDENTIAL. Default 'DRAFT'. */
    text?: string;
    /** Color with alpha. Default `rgba(220, 38, 38, 0.08)`. */
    color?: string;
    /** 0–1 opacity multiplier applied on top of the color's alpha. */
    opacity?: number;
  };

  // ── K5 Cover brand affordances (gated, optional) ───────────────
  /** Optional, OFF-by-default print-safe brand affordances on the cover:
   *  corner crop/registration marks and dotted "datum-strip" rulers (the
   *  engineering-instrument chrome). ABSENT on legacy/default themes ⇒ neither
   *  renders, so existing covers are byte-identical. A theme/template opts a
   *  single affordance in. See `lib/reportCoverTokens.ts`
   *  (`coverBrandAffordancesEnabled`). */
  coverBrandAffordances?: {
    /** Corner registration / crop marks at the four page corners. */
    cropMarks?: boolean;
    /** Dotted engineering "datum strip" rulers (the mono coordinate strips). */
    datumStrips?: boolean;
    /** Render the ~3 designated interior feature panels (ELCC / verdict hero /
     *  decision impact) on the theme-derived DARK surface, for document
     *  gravity/rhythm. OFF by default — opt-in only. The DarkFeaturePanel
     *  primitive degrades to a light bordered panel when this is off. */
    darkFeatureRhythm?: boolean;
  };
}

/**
 * Sleek Interface token contract (SLK pack family).
 *
 * The Sleek primitives (SleekSection, SleekKPI, SleekAlert,
 * SleekChartTooltip, DOCX equivalents) derive their tint ramps from
 * `tonedTheme(theme)` (see `src/lib/themeColor.ts`). A tenant who
 * customises `primaryColor` / `positiveColor` / `warningColor` /
 * `negativeColor` gets coherent 50/100/600/900 tints throughout the
 * report — they do NOT need to set individual tints.
 *
 * Slate-50 / slate-700 / slate-800 chrome (sidebar, dark info card,
 * table head, section-header bottom border) stays hard-coded in the
 * primitives — Sleek is "tinted brand inside a neutral chrome", not
 * "fully tenantable chrome". Cross-pack invariant.
 */
export const DEFAULT_THEME: ReportTheme = {
  companyName: 'Datacrate',
  logoUrl: null,
  primaryColor: '#06b6d4',
  headingColor: '#0f172a',
  bodyTextColor: '#475569',
  backgroundColor: '#ffffff',
  headingFont: 'Geist Variable, sans-serif',
  bodyFont: 'Geist Variable, sans-serif',
  sidebarColor: '#06b6d4',
  sidebarText: 'Strictly Confidential',
  footerText: 'Datacrate – BTM Feasibility Engine',
  footerSubtext: 'Confidential – For Authorized Use Only',
  footerEmail: 'info@datacrate.io',
  positiveColor: '#059669',
  warningColor: '#d97706',
  negativeColor: '#dc2626',
  pageSize: 'a4',
  density: 'comfortable',
  chartPalette: ['#0071df', '#ffab00', '#1D9E75', '#A32D2D', '#7F77DD'],
  tableStyle: 'lined',
  headingScale: 'editorial',
  coverLayout: 'hero-image',
  tocEnabled: true,
  figureNumberingPrefix: 'Figure ',
  pageNumberFormat: 'page-of-total',
  disclaimerByArchetype: {},
  // TSE-2 defaults — all back-compat (renderers fall back when unset).
  themeTypographyScale: 1,
  themeLocale: {
    dateFormat: 'YMD',
    numberFormat: '1,234.56',
    temperatureUnit: 'C',
    distanceUnit: 'metric',
    energyUnit: 'kWh-MWh',
  },
  themeWatermark: {
    enabled: false,
    text: 'DRAFT',
    color: '#dc2626',
    opacity: 0.08,
  },
};
